import React, {useMemo} from 'react'

import isMobileVersion from 'store/mobile'

import {Styles} from 'components/theme'

import noCoverImage from 'images/no-job-cover.png'

interface Props {
  blur?: number
  coverOpacity?: number
  grayScale?: number
  opaqueCoverColor?: string
  opaqueCoverGradient?: {
    left: string
    middle?: string
    right: string
  }
  romeId?: string
  style?: React.CSSProperties
}

const coverAll: React.CSSProperties = {
  bottom: 0,
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
}

export function getJobGroupCoverImageURL(romeId: string): string {
  return (isMobileVersion ? config.jobGroupImageSmallUrl : config.jobGroupImageUrl).
    replace('ROME_ID', romeId)
}

const JobGroupCoverImage: React.FC<Props> = (props: Props): React.ReactElement => {
  const {blur, coverOpacity = .4, grayScale, opaqueCoverColor = '#000',
    opaqueCoverGradient, romeId = '', style} = props
  const coverUrl = romeId ? getJobGroupCoverImageURL(romeId) : noCoverImage
  const filters = useMemo((): readonly string[] => {
    const filters: string[] = []
    if (blur) {
      filters.push(`blur(${blur}px)`)
    }
    if (grayScale) {
      filters.push(`grayscale(${grayScale}%)`)
    }
    return filters
  }, [blur, grayScale])
  const coverImageStyle = useMemo((): React.CSSProperties => ({
    ...coverAll,
    backgroundImage: coverUrl ? `url("${coverUrl}")` : 'inherit',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    zIndex: -2,
    ...(filters.length ? Styles.VENDOR_PREFIXED('filter', filters.join(' ')) : undefined),
  }), [filters, coverUrl])
  const containerStyle = useMemo((): React.CSSProperties => ({
    ...coverAll,
    ...style,
    ...(blur ? {overflow: 'hidden'} : undefined),
  }), [blur, style])
  const opaqueCoverStyle = useMemo((): React.CSSProperties => {
    const additionalStyle: React.CSSProperties = {}
    if (opaqueCoverGradient) {
      const gradientParts = ['104deg', opaqueCoverGradient.left]
      if (opaqueCoverGradient.middle) {
        gradientParts.push(opaqueCoverGradient.middle)
      }
      gradientParts.push(opaqueCoverGradient.right)
      additionalStyle.background = `linear-gradient(${gradientParts.join(', ')})`
    }
    return {
      ...coverAll,
      backgroundColor: opaqueCoverColor,
      opacity: coverOpacity,
      zIndex: -1,
      ...additionalStyle,
    }
  }, [coverOpacity, opaqueCoverColor, opaqueCoverGradient])
  return <div style={containerStyle}>
    {coverUrl ? <div style={coverImageStyle} /> : null}
    <div style={opaqueCoverStyle} />
  </div>
}


export default React.memo(JobGroupCoverImage)
